import {
  MantineReactTable,
  useMantineReactTable,
  MRT_GlobalFilterTextInput,
  MRT_ShowHideColumnsButton,
} from 'mantine-react-table'
import { ActionIcon as MantineActionButton } from '@mantine/core'
import { PenIcon, Trash } from 'lucide-react'
import { FC, ReactNode } from 'react'
import { Address, AddressListItem } from '@/shared/api/topli/TopliApi'
import { useAddressBookTable } from '../../lib/use-address-book-table'
import { useTranslation } from 'react-i18next'
import { getLocalization } from '@/widgets/LanguageSelect/lib/consts'

type AddressBookTableProps = {
  addButtonSlot: ReactNode
  onOpenEditModal: (row: AddressListItem) => void
  onOpenDeleteModal: (row: AddressListItem) => void
  onOpenMapModal: (row: AddressListItem) => void
}

export const AddressBookTable: FC<AddressBookTableProps> = props => {
  const { addButtonSlot, onOpenEditModal, onOpenDeleteModal, onOpenMapModal } = props
  const {
    pagination,
    columns,
    sorting,
    fetchedAddresses,
    totalRowCount,
    isErrorAddresses,
    isFetchingAddresses,
    isLoadingAddresses,
    setPagination,
    setSorting,
    handleSearchChange,
  } = useAddressBookTable(onOpenMapModal)
  const { i18n } = useTranslation()
  const localization = getLocalization(i18n.language)

  const table = useMantineReactTable({
    columns,
    data: fetchedAddresses,
    enableStickyHeader: false,
    enableEditing: true,
    manualPagination: true,
    onPaginationChange: setPagination,
    rowCount: totalRowCount,
    manualSorting: true,
    onSortingChange: setSorting,
    enableColumnActions: false,
    positionActionsColumn: 'first',
    localization,
    mantineSearchTextInputProps: {
      classNames: {
        root: 'w-full min-w-[calc(100vw-2rem)] lg:min-w-[16rem]',
        input: 'w-full',
      },
      variant: 'filled',
    },
    enableRowActions: true,
    onGlobalFilterChange: handleSearchChange,
    renderRowActions: ({ row }) => (
      <div className='flex flex-row space-x-1 pr-8'>
        <MantineActionButton
          onClick={() => onOpenEditModal(row.original)}
          className='text-blue bg-blue/10 hover:bg-blue hover:text-white rounded-full transition-all'
          variant='light'
        >
          <PenIcon size={16} />
        </MantineActionButton>
        <MantineActionButton
          onClick={() => onOpenDeleteModal(row.original)}
          className='text-red bg-red/10 hover:bg-red hover:text-white rounded-full transition-all'
          variant='light'
        >
          <Trash size={16} />
        </MantineActionButton>
      </div>
    ),
    renderTopToolbar: ({ table }) => {
      return (
        <div className='flex flex-row flex-wrap lg:flex-nowrap items-center justify-between mb-2 gap-y-4 lg:gap-y-0'>
          <MRT_GlobalFilterTextInput table={table} />
          <div className='flex flex-row items-center space-x-2 w-full lg:w-fit'>
            <MRT_ShowHideColumnsButton className='h-[2.5rem] w-[2.5rem] rounded-md' table={table} />
            {addButtonSlot}
          </div>
        </div>
      )
    },
    mantineToolbarAlertBannerProps: isErrorAddresses
      ? {
          color: 'red',
          children: 'Error loading data',
        }
      : undefined,
    mantinePaperProps: {
      shadow: 'none',
      withBorder: false,
    },
    mantineTableProps: {
      sx: {
        tableLayout: 'auto',
      },
    },
    mantineTableBodyProps: {
      sx: {
        '&: td': {
          minWidth: 90,
        },
        '& tr:hover': {
          backgroundColor: '#FFFFFF',
        },
      },
    },
    mantineTableBodyRowProps: {
      className: 'hover:bg-[#F4FAFD]/10',
    },
    state: {
      isLoading: isLoadingAddresses,
      pagination,
      sorting,
      showGlobalFilter: true,
      showAlertBanner: isErrorAddresses,
      showProgressBars: isFetchingAddresses,
    },
  })

  return <MantineReactTable table={table} />
}
